import React, { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { defaultZero, calculateColor } from '../../../utils/helpers';
import Summary from './summary';
import {
    Heading2,
    Heading3,
    FlexDiv,
    TextDefault,
    Block,
    StatusBadge,
    SpaceBetweenDiv,
    ThemedButton,
    Tag,
    palette,
} from '@awarego-ui/components';
import SemiCircleProgress from '../assessment/semi-circle-progress';
import { Link } from 'react-router-dom';
import NoItemsYet from '../no-items-yet';
import FullWidthLink from '../../FullWidthLink';
import RequireRole from '../../../containers/RequireRole';
import TableClient from '../../../components/table/TableClient';
import StyledLink from '../../../components/StyledLink';

function AssessmentInsightDashboard({
    dashboardData,
    assessments,
    insightsLabel = 'Insights',
    insightsHeader = 'Insights',
    loadingInBackground,
}) {
    const columns = useMemo(
        () => [
            {
                Header: 'Insight',
                accessor: 'name',
                Cell: (x) => (
                    <>
                        <StyledLink
                            to={`/assessment-insight/${x.row.original.id}/view`}
                            $hoverColor={palette.graphite.onyx}
                            $underlined={true}
                        >
                            {x.value && x.value.length > 0 ? (
                                x.value.length > 35 ? (
                                    <Tooltip title={x.value}>
                                        <span>
                                            <TextDefault bold>
                                                {x.value.substring(0, 35)}
                                                ...
                                            </TextDefault>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <TextDefault bold>{x.value}</TextDefault>
                                )
                            ) : (
                                <Tag text="No Name" />
                            )}{' '}
                        </StyledLink>
                        <StatusBadge
                            color={x.row.original.statusInfo.color}
                            text={x.row.original.statusInfo.label}
                            transparent={x.row.original.statusInfo.transparent}
                        />
                    </>
                ),
                disableSortBy: true,
            },
            {
                Header: '# Participants',
                accessor: (x) => x._rawData.totalEmployees,
                Cell: (x) => <TextDefault>{x.value}</TextDefault>,
                disableSortBy: true,
            },
            {
                Header: 'Completion',
                accessor: 'attendance',
                Cell: (x) => (
                    <TextDefault bold color={calculateColor(x.value)}>
                        {defaultZero(x.value)}%
                    </TextDefault>
                ),
                disableSortBy: true,
            },
            {
                Header: 'Resilience score',
                accessor: 'resilienceScore',
                Cell: (x) => (
                    <SemiCircleProgress
                        percentage={x.value}
                        width={48}
                        top={12}
                        halfCircle
                        percentageText
                        showNA={!x.row.original._rawData.started}
                    />
                ),
                tooltip:
                    'Average participant score across all assessed threat areas.',
                disableSortBy: true,
            },
        ],
        []
    );

    return assessments.length > 0 ? (
        <Block tablePadding>
            <FlexDiv alignCenter mb={16} ml={16} gap={8}>
                <Heading2 mb={0}>{insightsLabel}</Heading2>
                {loadingInBackground && (
                    <CircularProgress size={22} color="primary" />
                )}
            </FlexDiv>
            <FlexDiv alignCenter $noWrap>
                {dashboardData && (
                    <Summary
                        mainNumberText={'Active'}
                        mainNumber={dashboardData.active.value}
                        iconUrl={'/static/img/icon-dashboard-repeat.svg'}
                        iconAlt={'arrow in circle'}
                        item="assessment-insight"
                        completionRate={dashboardData.active.completion}
                        usersFinished={dashboardData.active.usersFinished}
                        usersTotal={dashboardData.active.usersTotal}
                    />
                )}
                {dashboardData && (
                    <Summary
                        mainNumberText={'Completed'}
                        mainNumber={dashboardData.completed.value}
                        iconUrl={'/static/img/icon-dashboard-completed.svg'}
                        iconAlt={'circle with tick inside'}
                        item="assessment-insight"
                        completionRate={dashboardData.completed.completion}
                        usersFinished={dashboardData.completed.usersFinished}
                        usersTotal={dashboardData.completed.usersTotal}
                    />
                )}
            </FlexDiv>
            <Heading3 my={24} ml={16}>
                {insightsHeader} performance
            </Heading3>
            <Block noBorder resetPadding resetMargin>
                <TableClient
                    columns={columns}
                    data={assessments}
                    hidePagination
                    link={(row) => `/assessment-insight/${row.original.id}/view`}
                    disableSort
                />
            </Block>
            <FullWidthLink to={`/assessment-insight`}>
                <ThemedButton
                    mode="secondary"
                    size="fullWidth"
                    text={`All ${insightsHeader.toLowerCase()}`}
                />
            </FullWidthLink>
        </Block>
    ) : (
        <NoItemsYet
            img={'/static/img/assessment-empty.svg'}
            imgAlt={'sneak peak into assessment insight'}
            title={`No ${insightsHeader.toLowerCase()} yet`}
            text={
                `Create a new ${insightsHeader.toLowerCase().replace(/s$/, '')} to measure and track audience cybersecurity knowledge and behavior.`
            }
            heading={insightsHeader}
            buttons={
                <RequireRole>
                    <SpaceBetweenDiv gap={24}>
                        <ThemedButton
                            mode="primary"
                            text="Create new"
                            as={Link}
                            flexGrow={1}
                            $center
                            to={`/assessment-insight/create`}
                        />
                    </SpaceBetweenDiv>
                </RequireRole>
            }
        />
    );
}

export default AssessmentInsightDashboard;
